import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Users, Shuffle, Filter, TrendingUp, Heart, X, Zap } from 'lucide-react';
import { SwipeableProductCard, Product, SwipeAction } from './SwipeableProductCard';
import { useSharedCart } from '@/components/shared-cart/SharedCartProvider';
import { useUserStore } from '@/stores/userStore';
import { formatCurrency } from '@/utils/formatting';
import { API_BASE_URL, authHeaders } from '@/services/config';

interface ProductDiscoveryInterfaceProps {
  vendorId: string;
  sessionId: string;
  className?: string;
}

interface DiscoverySession {
  id: string;
  currentProductIndex: number;
  products: Product[];
  participantActions: Map<string, Map<string, SwipeAction['action']>>; // userId -> productId -> action
  sessionStats: {
    totalProducts: number;
    totalSwipes: number;
    consensusProducts: Product[];
    participantProgress: Map<string, number>;
  };
}

export const ProductDiscoveryInterface: React.FC<ProductDiscoveryInterfaceProps> = ({
  vendorId,
  sessionId,
  className = '',
}) => {
  const { sendMessage, addMessageListener, onlineParticipants, sharedCart, addItem } = useSharedCart();
  const myId = useUserStore(state => state.user?.id) || 'guest';
  const [discoverySession, setDiscoverySession] = useState<DiscoverySession | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState({
    priceRange: [0, 1000] as [number, number],
    categories: [] as string[],
    tags: [] as string[],
  });
  const [showStats, setShowStats] = useState(false);
  // Why the last add was refused — e.g. the bill is already being paid.
  const [addError, setAddError] = useState<string | null>(null);
  // Votes on the card the party is looking at right now: userId -> choice.
  // Deliberately not persisted — a card is a moment, and the group moves on
  // as soon as everyone has called it.
  const [cardVotes, setCardVotes] = useState<Record<string, SwipeAction['action']>>({});
  // Guards the auto-advance so a card is only ever moved on once.
  const advancedFor = useRef<number | null>(null);
  // Lets the auto-advance effect call the navigator without taking a
  // dependency on its identity, which changes on every deck update.
  const navigateToProductRef = useRef<((index: number) => void) | null>(null);

  // Load the vendor's real catalog
  useEffect(() => {
    let cancelled = false;

    const loadCatalog = async () => {
      setIsLoading(true);
      try {
        const res = await fetch(`${API_BASE_URL}/vendors/${vendorId}/sync/products?limit=50`, {
          headers: authHeaders(),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const body = await res.json();
        const products: Product[] = (body.products || []).map((p: any) => ({
          id: p.id,
          title: p.title,
          description: p.description || '',
          price: p.price,
          currency: p.currency || 'USD',
          images: p.images || [],
          vendorUrl: '',
          tags: [...(p.categories || []), ...(p.tags || [])],
          isInStock: p.availability !== 'out_of_stock',
        }));

        if (cancelled) return;
        setDiscoverySession({
          id: `discovery-${sessionId}`,
          currentProductIndex: 0,
          products,
          participantActions: new Map(),
          sessionStats: {
            totalProducts: products.length,
            totalSwipes: 0,
            consensusProducts: [],
            participantProgress: new Map(),
          },
        });
      } catch (err) {
        console.error('Failed to load catalog:', err);
        if (!cancelled) {
          setDiscoverySession({
            id: `discovery-${sessionId}`,
            currentProductIndex: 0,
            products: [],
            participantActions: new Map(),
            sessionStats: {
              totalProducts: 0,
              totalSwipes: 0,
              consensusProducts: [],
              participantProgress: new Map(),
            },
          });
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    loadCatalog();
    return () => {
      cancelled = true;
    };
  }, [sessionId, vendorId]);

  // Pick up the deck position the party is already on, so opening
  // discovery (or reloading) puts you on their card, not back at the start.
  useEffect(() => {
    if (!sharedCart) return;
    let cancelled = false;

    fetch(`${API_BASE_URL}/shared-carts/${sharedCart.id}/discovery`, { headers: authHeaders() })
      .then(res => (res.ok ? res.json() : null))
      .then(body => {
        const index = body?.data?.currentIndex;
        if (cancelled || typeof index !== 'number') return;
        setDiscoverySession(prev => (prev ? { ...prev, currentProductIndex: index } : prev));
      })
      .catch(() => {
        // Falls back to the first card, which is where a new party starts.
      });

    return () => {
      cancelled = true;
    };
  }, [sharedCart?.id]);

  // Everyone else's presence in the deck: their votes landing on the card
  // in front of you, and the group turning to the next one together.
  useEffect(() => {
    const unsubscribe = addMessageListener(message => {
      switch (message.type) {
        case 'product_swipe': {
          const voterId = message.userId;
          const action = message.data?.action as SwipeAction['action'] | undefined;
          const productId = message.data?.productId as string | undefined;
          if (!voterId || !action || !productId) return;

          setDiscoverySession(prev => {
            if (!prev) return prev;
            // Only count it if they are voting on the card we are showing.
            if (prev.products[prev.currentProductIndex]?.id !== productId) return prev;

            const actions = new Map(prev.participantActions);
            if (!actions.has(voterId)) actions.set(voterId, new Map());
            actions.get(voterId)!.set(productId, action);
            return { ...prev, participantActions: actions };
          });
          setCardVotes(prev => ({ ...prev, [voterId]: action }));
          break;
        }

        case 'discovery_advance': {
          const index = message.data?.currentIndex;
          if (typeof index !== 'number') return;
          setDiscoverySession(prev =>
            prev && prev.currentProductIndex !== index
              ? { ...prev, currentProductIndex: index }
              : prev
          );
          setCardVotes({});
          break;
        }

        case 'discovery_reset': {
          setDiscoverySession(prev =>
            prev ? { ...prev, currentProductIndex: 0, participantActions: new Map() } : prev
          );
          setCardVotes({});
          advancedFor.current = null;
          break;
        }
      }
    });

    return unsubscribe;
  }, [addMessageListener]);

  // Handle swipe actions
  const handleSwipe = useCallback((action: SwipeAction['action']) => {
    if (!discoverySession || !sharedCart) return;

    const currentProduct = discoverySession.products[discoverySession.currentProductIndex];
    if (!currentProduct) return;
    const userId = useUserStore.getState().user?.id || 'guest';

    // Record the vote. The card does not turn on one person's say-so any
    // more — the group moves when the group has called it.
    setCardVotes(prev => ({ ...prev, [userId]: action }));
    setDiscoverySession(prev => {
      if (!prev) return prev;

      const newParticipantActions = new Map(prev.participantActions);
      if (!newParticipantActions.has(userId)) {
        newParticipantActions.set(userId, new Map());
      }
      newParticipantActions.get(userId)!.set(currentProduct.id, action);

      return {
        ...prev,
        participantActions: newParticipantActions,
        sessionStats: {
          ...prev.sessionStats,
          totalSwipes: prev.sessionStats.totalSwipes + 1,
        },
      };
    });

    // Send real-time update to other participants
    sendMessage('product_swipe', {
      productId: currentProduct.id,
      action,
      productIndex: discoverySession.currentProductIndex,
    });

  }, [discoverySession, sharedCart, sendMessage]);

  // Who is actually in the room to vote. Guests and reconnecting shoppers
  // come and go, so the bar for "everyone has voted" is whoever is here now.
  const voters = useMemo(() => {
    const byId = new Map<string, string>();
    for (const participant of sharedCart?.participants ?? []) {
      const id = participant.userId || participant.guestId;
      if (id && participant.status === 'active') byId.set(id, participant.displayName);
    }
    // Always count yourself: presence can lag a fresh join.
    if (!byId.has(myId)) byId.set(myId, 'You');
    const online = Array.from(byId.entries()).filter(
      ([id]) => id === myId || onlineParticipants.size === 0 || onlineParticipants.has(id)
    );
    return online.map(([id, name]) => ({ id, name }));
  }, [sharedCart?.participants, onlineParticipants, myId]);

  const everyoneVoted = voters.length > 0 && voters.every(v => cardVotes[v.id]);

  // Handle add to cart
  const handleAddToCart = useCallback(async () => {
    if (!discoverySession) return;

    const currentProduct = discoverySession.products[discoverySession.currentProductIndex];
    if (!currentProduct) return;

    try {
      setAddError(null);
      await addItem(currentProduct.id, 1, undefined, `Added from discovery session`);

      // Send notification to participants
      sendMessage('product_added_to_cart', {
        productId: currentProduct.id,
        productTitle: currentProduct.title,
      });
    } catch (error) {
      setAddError(error instanceof Error ? error.message : 'Failed to add this to the cart');
    }
  }, [discoverySession, addItem, sendMessage]);

  // One shopper acts on consensus for the whole room. Every client can see
  // the votes now, so without a designated actor each would add the same
  // product and the quantity would climb by the size of the party.
  const consensusActor = useMemo(() => {
    const ids = voters.map(v => v.id).sort();
    const host = sharedCart?.hostUserId;
    return host && ids.includes(host) ? host : ids[0];
  }, [voters, sharedCart?.hostUserId]);

  const consensusHandled = useRef<string | null>(null);

  // When enough of the room loves the card, it goes in the cart on its own.
  useEffect(() => {
    if (!discoverySession) return;
    const product = discoverySession.products[discoverySession.currentProductIndex];
    if (!product || !product.isInStock) return;
    if (consensusHandled.current === product.id) return;
    // Consensus needs a room; on your own it is just shopping.
    if (voters.length < 2) return;

    const loveCount = voters.filter(v => cardVotes[v.id] === 'love').length;
    if (loveCount < Math.ceil(voters.length * 0.7)) return;

    consensusHandled.current = product.id;
    if (myId !== consensusActor) return;

    addItem(product.id, 1, undefined, `🎉 Consensus favourite! ${loveCount}/${voters.length} loved this`)
      .then(() => {
        sendMessage('consensus_reached', {
          productId: product.id,
          productTitle: product.title,
          loveCount,
          totalParticipants: voters.length,
        });
      })
      .catch((err: unknown) => {
        setAddError(err instanceof Error ? err.message : 'Could not add the group favourite');
      });
  }, [cardVotes, voters, consensusActor, myId, discoverySession, addItem, sendMessage]);


  // When the room has called the card, hold the tally for a beat so
  // everyone sees how it landed, then turn it together.
  useEffect(() => {
    if (!discoverySession || !everyoneVoted) return;
    const index = discoverySession.currentProductIndex;
    if (advancedFor.current === index) return;
    if (index >= discoverySession.products.length - 1) return;

    advancedFor.current = index;
    const timer = setTimeout(() => {
      navigateToProductRef.current?.(index + 1);
    }, 1400);
    return () => clearTimeout(timer);
  }, [everyoneVoted, discoverySession?.currentProductIndex, discoverySession?.products.length]);

  // Moving on is a group action: the server holds the deck position and
  // tells every screen to turn the card, so nobody drifts onto their own.
  const navigateToProduct = useCallback(
    async (index: number) => {
      if (!discoverySession || !sharedCart) return;
      const bounded = Math.max(0, Math.min(index, discoverySession.products.length - 1));

      // Move this screen straight away rather than waiting on the round
      // trip; the broadcast confirms it for everyone else.
      setDiscoverySession(prev => (prev ? { ...prev, currentProductIndex: bounded } : prev));
      setCardVotes({});

      try {
        await fetch(`${API_BASE_URL}/shared-carts/${sharedCart.id}/discovery/advance`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify({ currentIndex: bounded }),
        });
      } catch {
        // The card still turned locally; the next broadcast resyncs us.
      }
    },
    [discoverySession, sharedCart]
  );

  useEffect(() => {
    navigateToProductRef.current = navigateToProduct;
  }, [navigateToProduct]);

  // Reset session — takes the whole party back to the top of the deck.
  const resetDiscoverySession = useCallback(async () => {
    if (!sharedCart) return;
    setDiscoverySession(prev =>
      prev
        ? {
            ...prev,
            currentProductIndex: 0,
            participantActions: new Map(),
            sessionStats: { ...prev.sessionStats, totalSwipes: 0, consensusProducts: [] },
          }
        : prev
    );
    setCardVotes({});
    advancedFor.current = null;

    try {
      await fetch(`${API_BASE_URL}/shared-carts/${sharedCart.id}/discovery/reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
      });
    } catch {
      // Local reset stands; the broadcast will resync the others.
    }
  }, [sharedCart]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading products...</span>
      </div>
    );
  }

  if (!discoverySession) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-600">Failed to load discovery session</p>
      </div>
    );
  }

  if (discoverySession.products.length === 0) {
    return (
      <div className="py-12 text-center">
        <p className="font-medium text-ink">No products to browse yet</p>
        <p className="mt-1 text-sm text-ink/60">
          Connect a store in the backoffice and its catalog shows up here.
        </p>
      </div>
    );
  }

  const currentProduct = discoverySession.products[discoverySession.currentProductIndex];
  const hasMoreProducts = discoverySession.currentProductIndex < discoverySession.products.length - 1;
  const progress = ((discoverySession.currentProductIndex + 1) / discoverySession.products.length) * 100;

  // Get participant actions for current product
  const currentProductActions = new Map<string, SwipeAction['action']>();
  for (const [userId, actions] of discoverySession.participantActions.entries()) {
    const action = actions.get(currentProduct?.id || '');
    if (action) {
      currentProductActions.set(userId, action);
    }
  }

  return (
    <div className={`product-discovery-interface ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <h2 className="text-2xl font-bold text-gray-900">Product Discovery</h2>
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Users className="w-4 h-4" />
            <span>{voters.length} shopping together</span>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowStats(!showStats)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-all"
          >
            <TrendingUp className="w-4 h-4" />
            Stats
          </button>

          <button
            onClick={resetDiscoverySession}
            className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-all"
          >
            <Shuffle className="w-4 h-4" />
            Restart
          </button>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">
            Product {discoverySession.currentProductIndex + 1} of {discoverySession.products.length}
          </span>
          <span className="text-sm text-gray-500">
            {Math.round(progress)}% complete
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Stats Panel */}
      {showStats && (
        <div className="mb-6 bg-white rounded-xl shadow-lg p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Session Stats</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600 mb-1">
                {Array.from(discoverySession.participantActions.values())
                  .reduce((total, actions) => total + Array.from(actions.values())
                  .filter(action => action === 'love').length, 0)}
              </div>
              <div className="text-sm text-green-700 flex items-center justify-center gap-1">
                <Heart className="w-4 h-4 fill-current" />
                Loved Items
              </div>
            </div>

            <div className="text-center p-4 bg-red-50 rounded-lg">
              <div className="text-2xl font-bold text-red-600 mb-1">
                {Array.from(discoverySession.participantActions.values())
                  .reduce((total, actions) => total + Array.from(actions.values())
                  .filter(action => action === 'pass').length, 0)}
              </div>
              <div className="text-sm text-red-700 flex items-center justify-center gap-1">
                <X className="w-4 h-4" />
                Passed Items
              </div>
            </div>

            <div className="text-center p-4 bg-yellow-50 rounded-lg">
              <div className="text-2xl font-bold text-yellow-600 mb-1">
                {Array.from(discoverySession.participantActions.values())
                  .reduce((total, actions) => total + Array.from(actions.values())
                  .filter(action => action === 'maybe').length, 0)}
              </div>
              <div className="text-sm text-yellow-700 flex items-center justify-center gap-1">
                <Zap className="w-4 h-4" />
                Maybe Items
              </div>
            </div>
          </div>
        </div>
      )}

      {addError && (
        <div className="mx-auto mb-4 max-w-md rounded-xl bg-red-50 px-4 py-3 text-center text-sm text-red-700">
          {addError.charAt(0).toUpperCase() + addError.slice(1)}
        </div>
      )}

      {/* The room, calling this card. Everyone watches everyone else decide. */}
      <div className="mx-auto mb-4 max-w-md rounded-xl border border-line bg-white px-4 py-3">
        <div className="mb-2 flex items-center justify-between text-xs">
          <span className="font-medium uppercase tracking-wide text-ink/40">
            {everyoneVoted ? "That's everyone" : `${Object.keys(cardVotes).length} of ${voters.length} called it`}
          </span>
          {everyoneVoted && <span className="text-ink/40">Next card…</span>}
        </div>
        <div className="flex items-center gap-2">
          {voters.map(voter => {
            const vote = cardVotes[voter.id];
            const tone =
              vote === 'love'
                ? 'bg-aisle text-white'
                : vote === 'maybe'
                ? 'bg-yellow-400 text-ink'
                : vote === 'pass'
                ? 'bg-ink/30 text-white'
                : 'bg-paper text-ink/30 animate-pulse';
            return (
              <div
                key={voter.id}
                title={vote ? `${voter.name} · ${vote}` : `${voter.name} is deciding`}
                className={`flex h-8 w-8 items-center justify-center rounded-full font-display text-xs font-bold ${tone}`}
              >
                {vote === 'love' ? '♥' : vote === 'pass' ? '✕' : vote === 'maybe' ? '?' : voter.name.charAt(0).toUpperCase()}
              </div>
            );
          })}
        </div>
      </div>

      {/* Main Product Card */}
      <div className="relative mb-8">
        {currentProduct ? (
          <SwipeableProductCard
            product={currentProduct}
            onSwipe={handleSwipe}
            onAddToCart={handleAddToCart}
            participantActions={currentProductActions}
            isCurrentCard={true}
            className="max-w-md mx-auto"
          />
        ) : (
          <div className="text-center py-12 bg-white rounded-2xl shadow-lg">
            <div className="text-6xl mb-4">🎉</div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">
              Discovery Complete!
            </h3>
            <p className="text-gray-600 mb-6">
              You've seen all the products. Check your shared cart or start over!
            </p>
            <button
              onClick={resetDiscoverySession}
              className="bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-blue-700 transition-all"
            >
              Discover More Products
            </button>
          </div>
        )}
      </div>

      {/* Navigation */}
      {currentProduct && (
        <div className="flex justify-center gap-4">
          <button
            onClick={() => navigateToProduct(discoverySession.currentProductIndex - 1)}
            disabled={discoverySession.currentProductIndex === 0}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          
          {hasMoreProducts && (
            <button
              onClick={() => navigateToProduct(discoverySession.currentProductIndex + 1)}
              className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-all"
            >
              Skip This Product
            </button>
          )}
        </div>
      )}
    </div>
  );
};